/**
 * Data Files Page
 * მონაცემთა ფაილების გვერდი
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  DataTable,
  Modal,
  Spinner,
  useNotification
} from '@aiia/portal-core';
import type { TableColumn } from '@aiia/portal-core';
import {
  useDataFiles,
  useUploadDataFile,
  useDeleteDataFile,
  useReprocessDataFile
} from '../hooks/useDataFiles';
import type { DataFile } from '../features/api/dataFiles';
import { getApiErrorMessage } from '../utils/errorMessages';

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export default function DataFilesPage() {
  const { t } = useTranslation();
  const notification = useNotification();
  const { dataFiles, isLoading, refetch } = useDataFiles();
  const uploadDataFile = useUploadDataFile();
  const deleteDataFile = useDeleteDataFile();
  const reprocessDataFile = useReprocessDataFile();

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-refresh when files are being processed
  const hasProcessingFiles = dataFiles?.some(
    (f) => f.status === 'PENDING' || f.status === 'PROCESSING'
  );

  useEffect(() => {
    if (hasProcessingFiles) {
      const interval = setInterval(() => {
        refetch();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [hasProcessingFiles, refetch]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploadProgress(0);
      await uploadDataFile.mutateAsync({
        file: selectedFile,
        onProgress: setUploadProgress,
      });
      notification.success(t('dataFiles.uploadSuccess'));
      setIsUploadModalOpen(false);
      setSelectedFile(null);
      setUploadProgress(0);
      refetch();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
      setUploadProgress(0);
    }
  };

  const confirmDelete = (fileId: string) => {
    setFileToDelete(fileId);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!fileToDelete) return;
    try {
      await deleteDataFile.mutateAsync(fileToDelete);
      notification.success(t('dataFiles.deleteSuccess'));
      setDeleteModalOpen(false);
      setFileToDelete(null);
      refetch();
    } catch (error: unknown) {
      notification.error(t('dataFiles.deleteFailed'));
    }
  };

  const handleReprocess = async (fileId: string) => {
    try {
      await reprocessDataFile.mutateAsync(fileId);
      notification.success(t('dataFiles.reprocessStarted'));
      refetch();
    } catch (error: unknown) {
      notification.error(t('dataFiles.reprocessFailed'));
    }
  };

  const getStatusBadge = (status: string, errorMessage?: string) => {
    const styles: Record<string, string> = {
      PENDING: 'bg-yellow-100 text-yellow-700',
      PROCESSING: 'bg-blue-100 text-blue-700 animate-pulse',
      PROCESSED: 'bg-green-100 text-green-700',
      READY: 'bg-green-100 text-green-700',
      FAILED: 'bg-red-100 text-red-700',
      DELETED: 'bg-gray-100 text-gray-700',
    };

    const statusLabels: Record<string, string> = {
      PENDING: t('dataFiles.statusPending'),
      PROCESSING: t('dataFiles.statusIndexing'),
      PROCESSED: t('dataFiles.statusIndexed'),
      READY: t('dataFiles.statusIndexed'),
      FAILED: t('dataFiles.statusFailed'),
      DELETED: t('dataFiles.statusDeleted'),
    };

    return (
      <div className="flex flex-col">
        <span className={`px-2 py-1 text-xs rounded-full inline-block w-fit ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
          {statusLabels[status] || status}
        </span>
        {status === 'FAILED' && errorMessage && (
          <span className="text-xs text-red-600 mt-1 max-w-xs truncate" title={errorMessage}>
            {errorMessage}
          </span>
        )}
      </div>
    );
  };

  const columns: TableColumn<DataFile & Record<string, unknown>>[] = [
    {
      key: 'fileName',
      header: t('dataFiles.fileName'),
      render: (value) => (
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="font-medium truncate max-w-xs" title={String(value)}>{String(value)}</span>
        </div>
      ),
    },
    {
      key: 'fileType',
      header: t('dataFiles.type'),
      render: (value) => (
        <span className="text-sm text-gray-500">{String(value).toUpperCase()}</span>
      ),
    },
    {
      key: 'fileSize',
      header: t('dataFiles.size'),
      render: (value) => formatFileSize(Number(value)),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (value, row) => getStatusBadge(String(value), row.errorMessage as string | undefined),
    },
    {
      key: 'chunkCount',
      header: t('dataFiles.chunks'),
      render: (value, row) => {
        const status = row.status as string;
        const chunkCount = typeof value === 'number' ? value : 0;
        if (status === 'READY' || status === 'PROCESSED') {
          return (
            <span className="text-sm text-green-600 font-medium">
              {t('dataFiles.chunksCount', { count: chunkCount })}
            </span>
          );
        }
        if (status === 'PROCESSING') {
          return <span className="text-xs text-blue-500">{t('dataFiles.processing')}</span>;
        }
        return <span className="text-xs text-gray-400">-</span>;
      },
    },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (_, row) => (
        <div className="flex gap-2">
          {(row.status === 'FAILED' || row.status === 'PENDING') && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => handleReprocess(row.id as string)}
              disabled={reprocessDataFile.isPending}
            >
              {row.status === 'FAILED' ? t('common.retry') : t('common.process')}
            </Button>
          )}
          <Button
            size="sm"
            variant="danger"
            onClick={() => confirmDelete(row.id as string)}
            disabled={row.status === 'PROCESSING'}
          >
            {t('common.delete')}
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('dataFiles.title')}</h1>
          <p className="text-gray-600 mt-1">{t('dataFiles.subtitle')}</p>
        </div>
        <Button onClick={() => setIsUploadModalOpen(true)}>
          {t('dataFiles.uploadFile')}
        </Button>
      </div>

      {/* Processing indicator */}
      {hasProcessingFiles && (
        <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          <div>
            <p className="font-medium text-blue-800">{t('dataFiles.filesIndexing')}</p>
            <p className="text-sm text-blue-600">{t('dataFiles.filesIndexingDesc')}</p>
          </div>
        </div>
      )}

      <Card className="p-6 border-2 border-dashed border-gray-200 bg-gray-50">
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p className="text-gray-600 mb-2">{t('dataFiles.dragDropText')}</p>
          <Button variant="secondary" onClick={() => setIsUploadModalOpen(true)}>
            {t('dataFiles.browseFiles')}
          </Button>
          <p className="text-sm text-gray-500 mt-2">
            {t('dataFiles.supportedFormats')}
          </p>
        </div>
      </Card>

      <Card>
        <DataTable
          columns={columns}
          data={(dataFiles || []) as (DataFile & Record<string, unknown>)[]}
          emptyMessage={t('dataFiles.emptyMessage')}
        />
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setFileToDelete(null);
        }}
        title={t('dataFiles.deleteTitle')}
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            {t('dataFiles.deleteConfirm')}
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setDeleteModalOpen(false);
                setFileToDelete(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleteDataFile.isPending}>
              {deleteDataFile.isPending ? t('common.deleting') : t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Upload Modal */}
      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false);
          setSelectedFile(null);
          setUploadProgress(0);
        }}
        title={t('dataFiles.uploadTitle')}
      >
        <div className="space-y-4">
          <div
            className="p-8 border-2 border-dashed border-gray-300 rounded-lg text-center cursor-pointer hover:border-blue-500 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.docx,.doc,.txt,.csv,.json"
              onChange={handleFileSelect}
            />
            {selectedFile ? (
              <div>
                <svg className="w-12 h-12 mx-auto text-green-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="font-medium text-gray-900">{selectedFile.name}</p>
                <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
              </div>
            ) : (
              <div>
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-gray-600">{t('dataFiles.clickToSelect')}</p>
                <p className="text-sm text-gray-500 mt-1">{t('dataFiles.fileFormats')}</p>
              </div>
            )}
          </div>

          {uploadProgress > 0 && uploadProgress < 100 && (
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>{t('common.uploading')}</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsUploadModalOpen(false);
                setSelectedFile(null);
                setUploadProgress(0);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploadDataFile.isPending}
            >
              {uploadDataFile.isPending ? t('common.uploading') : t('common.upload')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
